import { Injectable } from '@angular/core';
import {
  AddEditUserDialogComponentContentful,
  DataWindowComponentContentful,
  DataWindowComponentScoutingContentful,
  EditFieldsComponentContentful,
  FarmsComponentContentful, FeatureToggles,
  GeneralDialogContentful,
  GeneralPageContentful,
  GeneralDataWindowComponentContentful,
  GroupLeaderAddDialogContentful, LandingPageComponentContentful,
  LandingPageContentful,
  LoginFormContentful,
  NavbarNavigationContentful,
  NewFarmComponentContentful,
  GeneralSidebarPageContentful,
  RequestDroneServiceDialogComponentContentful,
  SetPasswordComponentContentful,
  SidebarComponentContentful,
  SidebarComponentReportingContentful, SignUpContactFormContentful,
  UploadComponentContentful,
  UserManagementComponentContentful, WebsiteFooterComponentContentful,
  CampaignPageContentful, ReportingComponentContentful, OrchardOverviewContentful,
  ClientRoleTypeContentful, GeneralComponentContentful, PlainTextContentful, GeneralFormContentful
} from '../shared/models/contentful.models';
import { ContentfulService } from './contentful.service';
import { DataService } from './data.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

interface Content {
  language?: string;
}

interface AddEditUserDialogComponentContent extends Content {
  data: AddEditUserDialogComponentContentful;
  transformData: BehaviorSubject<boolean>;
}

interface PlainTextContent extends Content {
  data: Array<PlainTextContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface CampaignPageContent extends Content {
  data: Array<CampaignPageContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface ClientRoleTypeContent extends Content {
  data: Array<ClientRoleTypeContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface DataWindowComponentContent extends Content {
  data: DataWindowComponentContentful;
}

interface DataWindowComponentScoutingContent extends Content {
  data: DataWindowComponentScoutingContentful;
}

interface FarmsComponentContent extends Content {
  data: Array<FarmsComponentContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface FeatureTogglesContent {
  data: FeatureToggles;
}

interface EditFieldsComponentContent extends Content {
  data: EditFieldsComponentContentful;
}

interface GeneralDialogContent extends Content {
  data: Array<GeneralDialogContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface GeneralFormComponentContent extends Content {
  data: Array<GeneralFormContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface GeneralPageContent extends Content {
  data: Array<GeneralPageContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface GeneralDataWindowComponentContent extends Content {
  data: Array<GeneralDataWindowComponentContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface GroupLeaderAddDialogContent extends Content {
  data: GroupLeaderAddDialogContentful;
}

interface LandingPageContent extends Content {
  data: Array<LandingPageContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface LandingPageComponentContent extends Content {
  data: Array<LandingPageComponentContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface LoginFormContent extends Content {
  data: LoginFormContentful;
}

interface NavbarNavigationContent extends Content {
  data: Array<NavbarNavigationContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface NewFarmComponentContent extends Content {
  data: NewFarmComponentContentful;
}

interface ReportingComponentContent extends Content {
  data: ReportingComponentContentful;
}

interface RequestDroneServiceContent extends Content {
  data: RequestDroneServiceDialogComponentContentful;
}

interface GeneralSidebarPageContent extends Content {
  data: Array<GeneralSidebarPageContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface GeneralComponentContent extends Content {
  data: Array<GeneralComponentContentful>;
  transformData: BehaviorSubject<boolean>;
}

interface SetPasswordComponentContent extends Content {
  data: SetPasswordComponentContentful;
}

interface SidebarComponentContent extends Content {
  data: SidebarComponentContentful;
  transformData: BehaviorSubject<boolean>;
}

interface SidebarComponentReportingContent extends Content {
  data: SidebarComponentReportingContentful;
}

interface SignUpContactFormContent extends Content {
  data: SignUpContactFormContentful;
}

interface UploadComponentContent extends Content {
  data: UploadComponentContentful;
}

interface UserManagementComponentContent extends Content {
  data: UserManagementComponentContentful;
}

export interface OrchardOverviewComponentContent extends Content {
  data: OrchardOverviewContentful;
}

interface WebsiteFooterComponentContent extends Content {
  data: WebsiteFooterComponentContentful;
  transformData: BehaviorSubject<boolean>;
}

/** Each property of this contentful cache service matches directly to the content_type_id in contentful **/
@Injectable({
  providedIn: 'root'
})
export class ContentfulCacheService {
  public addEditUserDialogComponent: AddEditUserDialogComponentContent = {
    transformData: new BehaviorSubject(false),
    data: {
      addHeader: '',
      editHeader: '',
      namePlaceholder: '',
      nameError: '',
      numberPlaceholder: '',
      numberError: '',
      emailPlaceholder: '',
      emailError: '',
      roleHeader: '',
      rolesTableAccess: '',
      rolesTableDescription: '',
      rolesTableName: '',
      roles: [],
      selectFarmsPlaceholder: '',
      selectFarmsErrorMultiple: '',
      selectFarmsErrorRequired: '',
      selectParentUserPlaceholder: '',
      selectParentUserError: '',
      updateButtonText: '',
      cancelButtonText: '',
      inviteButtonText: '',
      errorInvitingSnackbar: '',
      errorUpdatingSnackbar: '',
      farmAccessUpdateSuccess: '',
      userUpdateSuccess: ''
    }
  };
  public plainText: PlainTextContent = {
    data: [],
    transformData: new BehaviorSubject(false),
  };
  public campaignPage: CampaignPageContent = {
    data: [],
    transformData: new BehaviorSubject(false),
  };
  public clientRoleType: ClientRoleTypeContent = {
    data: [],
    transformData: new BehaviorSubject(false),
  };
  public dataWindowComponent: DataWindowComponentContent = {
    data: {
      treeDataHeader: 'tree data',
      treeDataRemoveTooltip: 'Remove tree overlays',
      treeDataNumTrees: 'No. trees',
      treeDataCanopyCover: 'Canopy cover',
      treeDataRowSpacing: 'Row spacing',
      treeDataTreeSpacing: 'Tree spacing',
      treeDataToggleMissingTrees: 'Show missing trees',
      orchardsOverviewHeader: 'Orchards tree data overview',
      selectedTreeHeader: 'Selected tree',
      selectedTreeRemoveTooltip: 'Close tree data',
      selectedTreeHealth: 'NDVI:',
      selectedTreeCi: 'Health:',
      selectedTreeArea: 'Area:',
      selectedTreeVolume: 'Volume:',
      selectedTreeCoordinate: 'Coordinate:',
      satelliteChartHeader: 'index data',
      satelliteChartRemoveTooltip: 'Remove satellite graph',
      satelliteChartFetchButton: 'Fetch satellite graph data',
      satelliteChartEnlargeTooltip: 'Enlarge satellite graph',
      clickedMarkerHeader: 'Marker',
      clickedMarkerRemoveTooltip: 'Close currently active marker',
      clickedMarkerType: 'Type:',
      clickedMarkerCount: 'Count:',
      clickedMarkerDate: 'Date:',
      clickedMarkerCoordinate: 'Coordinate:',
      clickedMarkerTextPlaceholder: 'Marker text',
      zonesNumberOf: 'Number of zones',
      zonesTableHeaderZone: 'Zone',
      zonesTableHeaderArea: 'Area',
      zonesMapTypeLayers: {
        area: 'Area',
        ci: 'Health',
        ndvi: 'NDVI',
        volume: 'Volume'
      },
      zonesTableHeaderPrescriptions: 'Prescriptions',
      zonesTableHeaderTotal: 'Total',
      zonesUpdatePrescriptions: 'Update Prescriptions',
      zonesUpdateFailed: 'Update failed',
      zonesFieldsNotValid: 'Some fields are not valid',
      zonesDownloadShapefile: 'Download Shapefile',
      zonesDownloadShapefileFailed: 'Failed to download shapefile',
      overviewDataNotAvailable: 'No Available Data',
      cropTypeFilterLabel: 'Crop Type Filter',
      orchardFilterCropTypePlaceholder: 'Crops',
      orchardFilterSelectAll: 'Select All',
      orchardFilterDeselectAll: 'Deselect All',
      treeHistogramActionsHeader: 'For current selection:',
      treeHistogramButtonOne: 'Create Scout Route',
      treeHistogramButtonTwo: 'Create Group',
      measurementsLength: 'Length',
      measurementsCancel: 'Stop measuring',
      measurementsRedraw: 'Redraw polygon',
    }
  };
  public dataWindowComponentScouting: DataWindowComponentScoutingContent = {
    data: {
      routePlanningHeader: 'Scout route planning',
      routePlanningRemoveTooltip: 'Exit route planning mode',
      routePlanningNumberWaypoints: 'Number of route waypoints:',
      routePlanningTitlePlaceholder: 'Route title',
      routePlanningTitleError: 'Please give your route a title',
      routePlanningSaveButton: 'Save route',
      routePlanningCancelButton: 'Cancel',
      scoutRoutesHeader: 'My scout routes',
      scoutRoutesRemoveTooltip: 'Clear scouting data',
      scoutRoutesFilters: 'Route filters',
      scoutRoutesFilter1: 'Only show data for selected orchard',
      scoutRoutesFilter2: 'Only show completed scouts',
      scoutRoutesAddNewTooltip: 'Add a new scout route',
      scoutRoutesPlannedRoutes: 'Planned routes',
      scoutRoutesNoRoutesPlanned: 'No routes planned yet',
      scoutRoutesScheduleTooltip: 'Schedule route for scouting',
      scoutRoutesViewRouteOnMap: 'Click to view route on map',
      scoutRoutesScoutComplete: 'complete',
      scoutRoutesScoutIncomplete: 'incomplete',
      scoutRoutesScoutAssignedTo: 'Assigned to',
      scoutRoutesScoutEnableMarkers: 'Enable scouted markers',
      scoutRoutesScoutDisableMarkers: 'Disable scouted markers',
      scoutRoutesNoRouteAssigned: 'No route assigned',
      scoutRouteSaveNameTooltip: 'Save name',
      scoutRouteCancelEditTooltip: 'Cancel',
      scoutRouteEditNameTooltip: 'Edit route name',
      scoutRouteEditWaypointsTooltip: 'Edit route waypoints',
      scoutRouteArchiveTooltip: 'Delete route',
      scoutRouteConfirmDeleteMessage: 'Are you sure you want to delete this route?',
      scoutMissionConfirmDeleteMessage: 'Are you sure you want to delete this scout?',
      scoutMissionArchiveTooltip: 'Delete scout',
      scoutMissionEditTooltip: 'Edit assignee and date',
      yieldSampleTypeHeader: 'What will you be measuring?',
      yieldSampleTypeSize: 'Size',
      yieldSampleTypeCount: 'Count',
      yieldCountMethod: 'What count method will you be using?',
      yieldCountMethodBox: 'Box',
      yieldCountMethodFullTree: 'Full Tree',
      yieldNoSampleMethodSelected: 'Select at least one method',
      saveScoutMissionAsRouteTooltip: 'Save mission as scout route',
      saveScoutMissionAsRouteSuccess: 'Route created: ',
      saveScoutMissionAsRouteFail: 'Converting scout route to mission failed',
      indicateRouteCreatedFromScoutMission: 'scout mission route',
      viewRoutes: 'VIEW ROUTES',
      saveScoutRouteFailed: 'Creating scout route failed'
    }
  };
  public farmsComponent: FarmsComponentContent = {
    transformData: new BehaviorSubject(false),
    data: [
      {
        identifier: 'my-policies',
        welcomeHeader: 'Welcome to Aeroview',
        welcomeHeaderInactive: 'Your client account has been marked as <b>inactive</b> but you are seeing this page as a service provider',
        welcomeAction: 'Select the farm you want to view below',
        welcomeActionNoFarms: 'Please click the button below to add your first farm',
        welcomeActionServiceProvider: 'Please select the farm you want to upload data to',
        searchFarmPlaceholder: 'Search for a farm',
        searchClientPlaceholder: 'Search for a client',
        selectFarmOrchardNumber: 'Number of orchards:',
        selectFarmTotalArea: 'Total area:',
        selectFarmButton: 'Select farm',
        logOutButton: 'Log out',
        accountSettingsButton: 'Account settings',
        myFarmsButton: 'My farms',
        userManagementButton: 'User management',
        toggleSharedFarmsTooltip: 'Toggle shared farms',
        editFarmsTooltip: 'Edit farms',
        clientHierarchyTooltip: 'View client hierarchy',
        addFarmTooltip: 'Add new farm',
        addUserOrFarmTooltip: 'Add new user or farm'
      },
      {
        identifier: 'my-farms',
        welcomeHeader: 'Welcome to Aeroview',
        welcomeHeaderInactive: 'Your client account has been marked as <b>inactive</b> but you are seeing this page as a service provider',
        welcomeAction: 'Select the farm you want to view below',
        welcomeActionNoFarms: 'Please click the button below to add your first farm',
        welcomeActionServiceProvider: 'Please select the farm you want to upload data to',
        searchFarmPlaceholder: 'Search for a farm',
        searchClientPlaceholder: 'Search for a client',
        selectFarmOrchardNumber: 'Number of orchards:',
        selectFarmTotalArea: 'Total area:',
        selectFarmButton: 'Select farm',
        logOutButton: 'Log out',
        accountSettingsButton: 'Account settings',
        myFarmsButton: 'My farms',
        userManagementButton: 'User management',
        toggleSharedFarmsTooltip: 'Toggle shared farms',
        editFarmsTooltip: 'Edit farms',
        clientHierarchyTooltip: 'View client hierarchy',
        addFarmTooltip: 'Add new farm',
        addUserOrFarmTooltip: 'Add new user or farm'
      }
    ]
  };
  public editFieldsComponent: EditFieldsComponentContent = {
    data: {
      autosaveBusy: 'Saving...',
      autosaveDone: 'All changes up-to-date',
      autosaveError: 'Autosave has failed. Ensure stable internet connection and refresh the page',
      title: 'Add / edit your fields',
      goBackTooltip: 'I am finished editing',
      addNewTooltip: 'Add a new field',
      displayDroneDataToggle: 'Display visual drone data when editing field',
      editFieldsNewFarmInformation: 'You can finish adding all your fields at a later stage',
      tableName: 'Field name',
      tableCrop: 'Crop type',
      tableCultivar: 'Cultivar',
      tableRootstock: 'Rootstock',
      tableRowSpacing: 'Row Spacing',
      tableTreeSpacing: 'Tree Spacing',
      tableDatePlanted: 'Date Planted',
      tableSave: 'Save',
      tableChangeNameTooltip: 'Click to start typing',
      tableChangeCropPlaceholder: 'Change crop type',
      tableEditRedrawDeleteTooltip: 'Edit, redraw, or delete field',
      tableDeletingTooltip: 'Deleting field',
      tableSaveTooltip: 'Save changes',
      advancedBulkDeleteInformation: 'Deleting all fields in current farm please wait...',
      advancedBulkDeleteButton: 'Bulk delete all fields',
      // advancedSectionTitle: 'Advanced:  <strong>Load shapefile</strong>',
      // advancedSectionDescription: 'A shapefile zip containing a .prj, .shp and .dbf can be imported by dragging it into the box below.',
      // advancedDropZipBlock: 'Drop zip',
      // advancedShpCropPlaceholder: 'Shp crop type',
      // advancedShpFieldNamePlaceholder: 'Shp field name',
      // advancedShpExtractPolygonsButton: 'Get polygons',
      requestNewDialogTitleCultivar: 'Didn\'t find your cultivar?',
      requestNewDialogTitleRootstock: 'Didn\'t find your rootstock?',
      requestNewDialogBody: 'Request that it be added to the list.',
      requestNewDialogTitleButtonCancel: 'Cancel',
      requestNewDialogTitleButtonAction: 'Request',
      requestNewDialogTitleSubmitSuccessTitle: 'Request Submitted',
      requestNewDialogTitleSubmitSuccessBody: 'We\'ve received the request and will be in contact promptly.',
      requestNewDialogTitleSubmitErrorTitle: 'Something went wrong.',
      requestNewDialogTitleSubmitErrorBody: 'Kindly try again in a while.',
      requestNewDialogSubmittedClose: 'Close',
      requestDialogPlaceholderRootstock: 'Rootstock',
      requestDialogPlaceholderCultivar: 'Cultivar',
      preventBoundaryEditTooltip: 'Orchard contains survey data',
      actionAddOrchard: 'Add more',
      actionBulkEdit: 'Bulk edit',
      actionUploadFile: 'Upload File',
      tableStatus: 'Status',
      tableStatusDraft: 'Draft',
      tableStatusPublished: 'Published',
      tableActions: {
        edit: 'Edit',
        reviewDraft: 'Review draft',
        redraw: 'Redraw',
        delete: 'Delete'
      }
    }
  };
  public featureToggles: FeatureTogglesContent = {
    data: {
      yieldScoutMission: {
        allowedEnvironments: []
      },
      reportingTrendLines: {
        allowedEnvironments: []
      },
      sidebarYield: {
        yieldProductCropTypeIds: []
      },
      orchardOverview: {
        allowedEnvironments: []
      },
      treeAgeReporting: {
        allowedClientIds: []
      },
      orchardOverviewCompare: {
        allowedEnvironments: []
      },
      insurancePolicies: {
        allowedEnvironments: []
      },
      yieldVideos: {
        allowedEnvironments: []
      },
      soilMaps: {
        allowedEnvironments: []
      },
      snapshot: {
        allowedEnvironments: []
      },
      discussions: {
        allowedEnvironments: []
      },
      thermalLayer: {
        allowedClientIds: []
      },
      wsiSymbolicTreeLayer: {
        allowedEnvironments: []
      },
      transpirationSymbolicTreeLayer: {
        allowedEnvironments: []
      },
      yieldGrowthCurves: {
        allowedEnvironments: []
      },
      yieldHistoricalYield: {
        allowedEnvironments: []
      },
      yieldReports: {
        harvest_tonnes: {
          disallowedSurveyIds: []
        }
      },
      deleteTrees: {
        allowedClientIds: []
      },
      showNewPlatformOptInDialog: {
        allowedEnvironments: []
      },
      organisationYieldReports: {
        allowedClientIds: []
      }
    }
  };
  public generalDialog: GeneralDialogContent = {
    data: [],
    transformData: new BehaviorSubject<boolean>(false)
  };
  public generalFormComponent: GeneralFormComponentContent = {
    data: [],
    transformData: new BehaviorSubject<boolean>(false)
  };
  public generalPage: GeneralPageContent = {
    data: [],
    transformData: new BehaviorSubject(false),
  };
  public generalDataWindowComponent: GeneralDataWindowComponentContent = {
    data: [
      {
        identifier: 'MarkerDataWindow',
        textField1: 'Marker Info',
        textField2: 'Date: ',
        textField3: 'Coordinates: ',
        textField4: 'Count: ',
        textField5: 'Stage: ',
        textField6: 'Trap: ',
        textField7: 'Pictures',
        textField8: 'Intensity',
        textField9: 'Irrigation',
        textField10: 'Weeds',
        textField11: 'Other',
        textField12: '',
        textField13: '',
        textField14: '',
        textField15: ''
      },
      {
        identifier: 'SatelliteGraph',
        textField1: 'Date',
        textField2: 'NDVI',
        textField3: 'Moisture',
        textField4: 'True color',
        textField5: '',
        textField6: '',
        textField7: '',
        textField8: '',
        textField9: '',
        textField10: '',
        textField11: '',
        textField12: '',
        textField13: '',
        textField14: '',
        textField15: ''
      }
    ],
    transformData: new BehaviorSubject(false),
  };

  public groupLeaderAddDialog: GroupLeaderAddDialogContent = {
    data: {
      title: 'What would you like to add?',
      representativeHeader: 'Representative',
      representativeDescription: 'A representative can have multiple farmers beneath them',
      farmerHeader: 'Farmer',
      farmerDescription: 'A farmer is the bottom level user which uses the Aeroview system',
      farmHeader: 'Farm',
      farmDescription: 'A new farm linked to your account',
      optionOr: 'OR',
      addNewUserHeader: 'Add new user',
      addNewUserChoice: 'You have selected: ',
      addNewUserInstructions: 'Please enter the user details below. The user will then get an email ' +
      'with instructions to set their password',
      addNewUserFirstNamePlaceholder: 'First Name',
      addNewUserFirstNameError: 'First name required',
      addNewUserLastNamePlaceholder: 'Last Name',
      addNewUserLastNameError: 'Last name required',
      addNewUserEmailAddressPlaceholder: 'Email address',
      addNewUserEmailAddressError: 'Not a valid email address',
      addNewUserButtonText: 'Add user',
      addNewFarmHeader: 'Add new farm',
      addNewFarmInstructions: 'Please choose who you would like to add a farm for',
      addNewFarmButtonText: 'Add farm',
      snackBarSuccess: 'User added successfully they will receive an email with further instructions',
      snackBarError: 'Email address already exists for a user',
    }
  };
  public landingPage: LandingPageContent = {
    data: [{
      title: 'Tree crop protection for your farm',
      description: 'Early pest and disease detection guided by drone and satellite imagery',
      videoButton: 'Watch the video',
      registerOptions: [],
      identifier: 'default',
      metadataDescription: '',
      metadataKeywords: '',
      pageTitle: '',
    }],
    transformData: new BehaviorSubject(false),
  };
  public landingPageComponent: LandingPageComponentContent = {
    data: [{
      identifier: 'landing-page',
      formVisible: false,
      signUpFlow: true,
      title: 'Intelligent tools for perennial crops',
      titleMultipleLines: 'Intelligent<br>tools for<br>perennial<br>crops',
      description: 'Tree and fruit insights enabled by drone imagery and artificial intelligence',
      clientHeader: '',
      clientLogos: [],
      benefitsHeader: '',
      benefitsDescription: '',
      benefits: [],
      howItWorksHeader: '',
      howItWorksDescription: '',
      howItWorksEntries: [],
      callToActionBottomDescription: 'Start monitoring your trees today',
      callToActionBottomButton: 'Get in touch',
      pageTitle: '',
      metadataDescription: '',
      metadataKeywords: '',
      cookieConsentText: '',
      cookieConsentButton: '',
    }],
    transformData: new BehaviorSubject(false),
  };
  public loginForm: LoginFormContent = {
    data: {
      loginTabText: 'Log in',
      loginHeader: 'Welcome',
      loginDescription: 'We\'re happy to have you back',
      loginEmailPlaceholder: 'Email address',
      loginEmailError: 'Please enter a valid email address',
      loginPasswordPlaceholder: 'Password',
      loginPasswordError: 'Please enter a password',
      loginForgotPasswordText: 'Forgot password?',
      loginButtonText: 'Log in',
      signUpTabText: 'Sign up',
      signUpHeader: 'Join Aeroview now',
      signUpDescription: 'Get started for free!',
      signUpNamePlaceholder: 'Name',
      signUpNameError: 'Please add your name',
      signUpEmailPlaceholder: 'Email address',
      signUpEmailError: 'Please enter a valid email address',
      signUpPhonePlaceholder: 'Phone number',
      signUpPhoneError: 'Please enter a valid phone number',
      signUpPasswordPlaceholder: 'Password',
      signUpPasswordError: 'Please choose a password',
      signUpConfirmPasswordPlaceholder: 'Confirm your password',
      signUpConfirmPasswordError: 'Passwords do not match',
      signUpPromoCodePlaceholder: 'Promo code (optional)',
      signUpPromoCodeButton: 'Apply',
      signUpButtonText: 'Let\'s find my farm!',
      signUpTcs: 'By clicking this button, you agree to our <a tabindex="-1" target="_blank" ' +
      'href="https://about.aerobotics.io/privacy-policy">Privacy Policy</a> and <a tabindex="-1" ' +
      'target="_blank" href="https://about.aerobotics.io/terms-and-conditions">Terms and Conditions</a>',
      emailExistsSnackbar: 'Email address already exists for a user',
      passwordResetInstructionsSnackbar: 'An email has been sent to you with further instructions',
      emailPasswordErrorSnackbar: 'Email address or password incorrect',
    }
  };
  public navbarNavigation: NavbarNavigationContent = {
    data: [],
    transformData: new BehaviorSubject(false),
  };
  public newFarmComponent: NewFarmComponentContent = {
    data: {
      farmLocatorStepHeader: 'Where is your farm?',
      farmLocatorStepMapInstruction: 'Please draw your farm boundary by clicking around your farm on the map',
      farmLocatorStepMapInstructionMobile: 'Please tap farm boundary',
      farmLocatorStepSearchPlaceholder: 'Search for farm',
      farmLocatorStepSearchPlaceholderMobile: 'Search area',
      farmLocatorStepRedrawBoundaryButton: 'Redraw boundary',
      stepNextButton: 'Next',
      farmInformationStepHeader: 'Tell us about your farm',
      farmInformationStepFarmNameQuestion: 'what is your farm name?',
      farmInformationStepFarmNamePlaceholder: 'Farm name',
      farmInformationStepFarmCropQuestion: 'What do you farm?',
      farmInformationStepFarmCropError: 'Please select one or more crop families applicable to your farm',
      farmInformationStepDefaultCrop: 'Default farm crop:',
      farmInformationStepDefaultCropTooltip: 'Change default farm crop',
      farmEditFieldsStepHeader: 'Add / edit your fields',
      farmEditFieldsStepDescription: 'Automatically finding your fields, we\'ve got you covered ...',
      farmEditFieldsStepMobileFieldsFound: 'fields have been found for your farm!<br><br>If you would like to edit them, you can do ' +
      'that later when you\'re on a computer.<br><br>Click next to view your data',
      farmEditFieldsStepMobileNoFields: '<strong>You are nearly there!</strong><br><br>To view data please log into ' +
      'Aeroview on a computer and add the fields for your farm.',
      farmDataStepHeader: 'Let\'s see some data!',
      farmDataStepDescription: 'we are fetching historical data for your farm!',
      farmDataStepInformation: 'Did you know, we have a mobile app <strong>Aeroview SCOUT</strong> to help you scout your fields?',
      farmPolygonAccepted: 'Farm accepted:',
      farmPolygonTooLarge: 'Farm exceeds 5000ha',
      farmPolygonRedraw: 'Please redraw farm',
    }
  };
  public reportingComponent: ReportingComponentContent = {
    data: {
      pestTrapReportColHeaderFarmName: 'farm_name',
      pestTrapReportColHeaderOrchardName: 'orchard_name',
      pestTrapReportColHeaderTrapCoords: 'trap_coords',
      pestTrapReportColHeaderPestName: 'pest_name',
      pestTrapReportColHeaderInfestCount: 'infest_count',
      pestTrapReportColHeaderPestStage: 'pest_stage',
      pestTrapReportColHeaderDate: 'date',
      pestTrapReportColHeaderWeek: 'week_of_year',
      pestTrapReportColHeaderMonitorName: 'monitor_name',
      pestTrapReportColHeaderComments: 'comments',
      pestTrapReportColHeaderCropFamily: 'crop_family',
      pestTrapReportColHeaderCropType: 'crop_type',
      pestTrapReportOrchardDeletedMsg: 'orchard deleted',
      pestTrapReportPestStageNotProvided: 'not provided',
      pestTrapReportCol_IsPestTrap: 'Is Pest Trap',
      pestTrapReportCol_IsPestTrapYes: 'Yes',
      pestTrapReportCol_IsPestTrapNo: 'No',
      diseaseReportColDiseaseName: 'disease_name',
      diseaseReportColDiseaseIntensity: 'disease_intensity'
    }
  };
  public requestDroneServiceDialogComponent: RequestDroneServiceContent = {
    data: {
      dialogHeader: 'Request a drone service',
      dialogDescription: 'Please confirm the farm and select the orchards you want flown along with the desired date of the service',
      selectFarmPlaceholder: 'Select farm',
      selectOrchardsPlaceholder: 'Select orchards',
      selectDatePlaceholder: 'Please select desired date',
      additionalCommentsPlaceholder: 'Additional comments for service provider',
      promoCodePlaceholder: 'Promo code (optional)',
      promoCodeButtonText: 'Apply',
      requestServiceButton: 'Request service',
      snackBarSuccessful: 'Successfully requested a drone service, we will be in touch shortly!',
      snackBarError: 'Unable to request drone service, please try again later',
      snackBarAction: 'OK'
    }
  };
  public generalSidebarPage: GeneralSidebarPageContent = {
    transformData: new BehaviorSubject(false),
    data: []
  };

  public generalComponent: GeneralComponentContent = {
    transformData: new BehaviorSubject(false),
    data: []
  };

  public setPasswordComponent: SetPasswordComponentContent = {
    data: {
      headerConfirmAccount: 'Join <strong>Aeroview</strong> now',
      descriptionConfirmAccount: 'You are nearly there! Please take a moment to confirm your account by choosing a password',
      headerForgotPassword: 'Password reset',
      descriptionForgotPassword: 'Please choose a new password for your Aeroview account',
      passwordPlaceholder: 'Password',
      passwordError: 'Password required',
      confirmPasswordPlaceholder: 'Confirm password',
      confirmPasswordError: 'Passwords do not match',
      confirmAccountButton: 'Confirm account',
      forgotPasswordButton: 'Reset password',
      passwordTokenErrorSnackbar: 'Sorry this link has expired or token no longer valid',
    }
  };
  public sidebarComponent: SidebarComponentContent = {
    transformData: new BehaviorSubject(false),
    data: {
      modes: [],
      selectOrchardPlaceholder: 'Select orchard',
      selectDatePlaceholder: 'Select date',
      previousDateTooltip: 'Previous available date',
      nextDateTooltip: 'Next available date',
      mapTypeHeader: 'Map type',
      mapTypeDrone: 'Drone',
      mapTypeRequestDroneService: 'Request a drone flight',
      mapTypeSatellite: 'Satellite',
      mapTypeHighResSatellite: 'High Res Satellite',
      mapTypeLayers: [],
      treeDataHeader: 'Tree data',
      treeDataLayers: [],
      treeDataLayerUnavailable: 'Data Unavailable',
      treeDataModeTrees: 'Trees',
      treeDataModeZones: 'Zones',
      treeDataModeOrchards: 'Orchards',
      scoutingHeader: 'Scouting',
      scoutingNewRoute: 'New route',
      scoutingScoutRoutes: 'Scout routes',
      menuEditFields: 'Edit fields',
      menuUploadData: 'Upload drone data',
      menuExportData: 'Export data',
      menuProcessingStatus: 'Processing status',
      compareTreesButton: '',
      removeTreeComparisonButton: '',
      compareWithDatePlaceholder: '',
      treeCountsLayer: '',
      dataOverlayShowZones: 'Show Zones',
      dataOverlayShowZonesFeatureToggle: { allowedEnvironments: [] },
      treeGroupsHeader: '',
      treeGroupsPlaceholder: '',
      treeGroupsAddGroup: '',
      treeGroupsAllTrees: '',
      orchardOverview: '',
      orchardDetail: '',
      generatingZones: '',
      failedToGenerateZones: '',
      onboarding: {
        title: '',
        description: '',
        okText: ''
      },
      sideNavLabels: {
        mapping: {
          label: '',
          subnav: {
            overview: { label: '' },
            analysis: { label: '' }
          }
        },
        scouting: {
          label: '',
          subnav: {
            reports: { label: '' },
            manager: { label: '' }
          }
        },
        yield: {
          label: '',
          subnav: {
            reports: { label: '' },
            manager: { label: '' }
          }
        }
      },
      sideNavLabelsV2: {},
      menuUploadYieldData: '',
      closeSoilMap: '',
      noneSoilMapDropDown: '',
      soilMapTitle: '',
      addSoilMaps: '',
      soilMapsPlaceholder: '',
      soilMapLoadingError: '',
      treeGroupIrrigationLabel: '',
    }
  };
  public signUpContactForm: SignUpContactFormContent = {
    data: {
      fullNamePlaceholder: '',
      fullNameError: '',
      emailPlaceholder: '',
      emailError: '',
      passwordPlaceholder: '',
      passwordError: '',
      confirmPasswordPlaceholder: '',
      phonePlaceholder: '',
      phoneError: '',
      cropFamilyPlaceholder: '',
      cropFamilies: [],
      farmSizePlaceholder: '',
      userTypePlaceholder: '',
      userTypeOptions: [],
      locationPlaceholder: '',
      locationAdditionalPlaceholder: '',
      loginHeader: '',
      loginDescription: '',
      signUpHeader: '',
      signUpDescription: '',
      signUpButton: '',
      loginButton: '',
      getInTouchHeader: '',
      getInTouchTellUsMoreHeader: '',
      getInTouchCreatePasswordHeader: '',
      getInTouchDescription: '',
      getInTouchButton: '',
      getInTouchTellUsMoreButton: '',
      getInTouchCreatePasswordButton: '',
      forgotPassword: ''
    }
  };
  public sidebarComponentReporting: SidebarComponentReportingContent = {
    data: {
      reportingDataTypeHeader: 'Data type',
      reportingDataTypePest: 'Pest',
      reportingDataTypeDisease: 'Disease',
      reportingDataTypeDrone: 'Drone',
      reportingDataTypeWeed: 'Weed',
      reportingDataTypeIrrigation: 'Irrigation',
      reportingDataTypeNote: 'Note',
      reportingDataDisplayHeader: 'Data display',
      reportingDataDisplayLineGraph: 'Graph',
      reportingDataDisplayGraph: 'Table',
      reportingDataDisplayGraphUnavailable: 'Table unavailable',
      reportingDataDisplayMarkers: 'Markers',
      reportingDataDisplayHeatmap: 'Hotspot',
      reportingDataDisplayStartDate: 'Start date',
      reportingDataDisplayStartDateError: 'Start date required',
      reportingDataDisplayEndDate: 'End date',
      reportingDataDisplayEndDateError: 'End date required',
      reportingDataDisplayFilterHeader: 'Data filters',
      reportingDataDisplayFilterSelectAllNone: 'Select all / none',
      reportingExportDataHeader: 'Export data',
      reportingExportDataDownloadReport: 'Download report',
      reportingExportDataPestTrapReport: 'Pest trap report',
      reportingExportDataDownloadCsv: 'Download CSV',
      reportingExportDataDownloadFcmReport: 'False Codling Moth CSV',
      reportingExportDataDownloadPdf: 'PDF report',
      reportingDataThresholdHeader: 'Data threshold',
      reportingExportAllMarkers: 'Filtered Scout Data',
      reportFileGenericName: 'Filtered Scout Data',
      reportingYieldScoutHeader: 'Yield Scout',
      requestingPdf: 'Requesting PDF...',
      pdfReadyForDownload: 'Download PDF',
      threshold: 'Threshold',
      pestsLabel: 'Pests',
      diseasesLabel: 'Diseases',
      pestSingleSelectLabel: 'Pest',
      orchardSelectLabel: 'Orchards',
      filterForPestTrapLabel: 'Filter for pest traps',
      filterForPestTrapYes: 'Yes',
      filterForPestTrapNo: 'No',
    }
  };
  public uploadComponent: UploadComponentContent = {
    data: {
      uploaderText: 'Uploader',
      exitUploaderButtonText: 'Exit uploader',
      nextButtonText: 'Next',
      chooseCameraStepHeader: 'Choose your camera type',
      chooseCameraStepTitle: 'What do you fly?',
      chooseCameraStepContinueUploadHeader: 'Continuing with previous upload',
      chooseCameraStepContinueUploadDescription: 'The drone camera setup you selected for your previous upload will be used',
      chooseCameraStepDescription: 'Please select your drone camera setup below',
      chooseCameraStepOption1AdditionalInfo: 'No crop health data',
      chooseCameraStepError: 'Please choose a camera set up',
      addPhotosHeader: 'Add photos',
      addPhotosContinueDescription: 'Please add <strong>ALL</strong> your images. Images which have already been uploaded will ' +
      '<strong>NOT</strong> upload again.',
      addPhotosReadyForUpload: 'files ready for upload',
      addPhotosAlreadyAdded: 'files already uploaded',
      addPhotosAddError: 'Please add at least 4 GPS-referenced photos',
      addPhotosWaitError: 'Please wait until all photos have been loaded',
      addPhotosMSPError: 'Please ensure you add at least the Near Infrared and Red bands',
      imagesLoaded: 'images loaded',
      imagesRejected: 'images rejected. Scroll down to view image list',
      previousManifestNotMatched: 'The loaded images do not match the previous manifest',
      removeImageTooltip: 'Remove image',
      dragDropInformation: 'Please drag and drop files anywhere on the screen',
      dragOverText: 'Drop image files here',
      confirmOrchardsStepHeader: 'Confirm orchards to process',
      confirmOrchardsStepTitle: 'Please confirm the orchards you want processed',
      confirmOrchardsTableName: 'Name',
      confirmOrchardsTableArea: 'Area',
      confirmOrchardsIntersectError: 'Field self intersects, this needs to be corrected',
      confirmOrchardsNotEnoughCoverageError: 'Not enough coverage detected',
      confirmOrchardsWarningCoverage: 'Warning: might not be enough coverage',
      confirmOrchardsNoOrchardSelectedError: 'Please select at least one orchard to process',
      startUploadStepHeader: 'Start upload',
      startUploadStepSummary: 'Summary of upload',
      startUploadStepSummaryDescription: 'Below is a summary of your upload, please make sure everything is correct before uploading.',
      startUploadStepTableRGB: 'Number of visual files',
      startUploadStepTableMSP: 'Number of multi-spectral files',
      startUploadStepTableOrchards: 'Orchards to process',
      startUploadStepTableArea: 'Total area',
      startUploadStepBeforeUploadTitle: 'Before you upload',
      startUploadStepBeforeUploadDetails: '1. Do not close the browser or redirect from this page. Your upload will stop.\n\n' +
      '2. If you are uploading directly from a memory card do not remove the memory card.\n\n' +
      '3. By clicking upload, you acknowledge that no crop health data can be shown without multi-spectral images being uploaded.' +
      '<a href="https://about.aerobotics.io/buy-a-drone" target="_blank" style="color:#17293E">Click here</a> to ' +
      'request more information about drones and Multi-spectral (MSP) cameras.',
      startUploadStepStartButton: 'Start upload',
      startUploadUnknownError: 'An unknown error occurred please refresh the page and restart the upload',
      uploadingStageSomeFilesNotUploaded: 'Some files have not uploaded',
      uploadingStageUploadInProgress: 'Upload in progress:',
      uploadingStageSuccess: 'success',
      uploadingStageFailed: 'failed',
      uploadingStageRemaining: 'remaining',
      uploadingStageRetry: 'Retry failed uploads',
      uploadingStageError: 'Unable to complete upload. Please retry or contact us for support.',
      uploadingStageRetryCompletion: 'Retry upload completion',
    }
  };
  public userManagementComponent: UserManagementComponentContent = {
    data: {
      header: '',
      addNewUserPlaceholder: '',
      searchUserPlaceholder: '',
      tableHeaderName: '',
      tableHeaderEmail: '',
      tableHeaderNumber: '',
      tableHeaderRole: '',
      tableHeaderAction: '',
      tableActionButtonRemove: '',
      tableActionButtonEdit: '',
      tableActionButtonViewFarms: '',
      tableActionButtonEditAccess: ''
    }
  };
  public orchardOverviewComponent: OrchardOverviewComponentContent = {
    data: {
      id: '',
      tableHeaderOrchardName: '',
      tableHeaderSize: '',
      tableHeaderDatePlanted: '',
      tableHeaderCultivar: '',
      tableHeaderRootstock: '',
      selectLabelDataSource: '',
      selectLabelSurveyDate: '',
      selectLabelDroneSurveyDate: '',
      selectLabelShowChange: '',
      selectValueDrone: '',
      selectValueSatellite: '',
      filterDateBefore: '',
      filterDateAfter: '',
      filterDateUnknown: '',
      noSatelliteData: '',
      noCropTypeProvided: '',
      filterValues: '',
      selectAll: '',
      clear: '',
      cancel: '',
      done: '',
      choose: '',
      errorLoadingData: '',
      empty: '',
      noDroneData: '',
      tableHeaderTreeCountInfoTitle: '',
      tableHeaderTreeCountInfoDescription: '',
      tableHeaderMissingTreesInfoTitle: '',
      tableHeaderMissingTreesInfoDescription: '',
      tableHeaderAvgHealthInfoTitle: '',
      tableHeaderAvgHealthInfoDescription: '',
      tableHeaderHealthVarInfoTitle: '',
      tableHeaderHealthVarInfoDescription: '',
      tableHeaderAvgCanopyInfoTitle: '',
      tableHeaderAvgCanopyInfoDescription: '',
      tableHeaderAreaVarInfoTitle: '',
      tableHeaderAreaVarInfoDescription: '',
      tableHeaderSatelliteAvgHealthInfoTitle: '',
      tableHeaderSatelliteAvgHealthInfoDescription: '',
      tableHeaderSatelliteHealthVarInfoTitle: '',
      tableHeaderSatelliteHealthVarInfoDescription: '',
      tableHeadersForDrone: {
        tree_count: '',
        missing_trees: '',
        av_health: '',
        health_var: '',
        av_area: '',
        area_var: '',
        crop_type: ''
      },
      tableHeadersForSatellite: {
        av_health: '',
        health_var: '',
        crop_type: ''
      },
      calendarMonths: {
        january: '',
        february: '',
        march: '',
        april: '',
        may: '',
        june: '',
        july: '',
        august: '',
        september: '',
        october: '',
        november: '',
        december: ''
      },
      measurements: {
        m2: 'm²',
        ft2: 'ft²',
        ac: 'ac',
        ha: 'ha'
      },
      sort: '',
      overviewClickTour: {
        next: 'Next',
        prev: 'Prev',
        intro: {
          drone: {
            title: '',
            subtitle: ''
          },
          satellite: {
            title: '',
            subtitle: ''
          }
        },
        datasource: {
          drone: {
            title: '',
            subtitle: ''
          },
          satellite: {
            title: '',
            subtitle: ''
          }
        },
        date: {
          drone: {
            title: '',
            subtitle: ''
          },
          satellite: {
            title: '',
            subtitle: ''
          }
        },
        header: {
          drone: {
            title: '',
            subtitle: ''
          },
          satellite: {
            title: '',
            subtitle: ''
          }
        },
        orchard: {
          drone: {
            title: '',
            subtitle: ''
          },
          satellite: {
            title: '',
            subtitle: ''
          }
        }
      },
      export: ''
    }
  };
  public websiteFooterComponent: WebsiteFooterComponentContent = {
    data: {
      footerNavigationItems: [],
      promotionImage: '',
      promotionUrl: '',
      facebookUrl: '',
      linkedInUrl: '',
      youtubeUrl: '',
      instagramUrl: '',
      twitterUrl: '',
      copyrightText: ''
    },
    transformData: new BehaviorSubject<boolean>(false)
  };

  constructor(private contentful: ContentfulService, private dataService: DataService) { }

  /** Function to fetch content based on a content type id and update accordingly **/
  fetchContent(contentTypeId) {
    // Assign the content to the fields of the first entry (there should only be one) of the SidebarComponent from contentful
    if (this.hasOwnProperty(contentTypeId)) {
      const languageCode = this[contentTypeId].language;
      const selectedLanguageCode = this.dataService.selectedLanguage.code;
      if (languageCode !== selectedLanguageCode) {
        this.contentful.getAndObserveContent(contentTypeId).subscribe(content => {
          this.updateContentCache(content, contentTypeId, selectedLanguageCode);
        });
      }
    }
  }

  /** Function to update the content cache based off the data from contentful **/
  updateContentCache(content, contentTypeId, selectedLanguageCode) {
    const cachedContentIsArray = Array.isArray(this[contentTypeId].data);
    // If the cached content is an array already then reset it
    if (cachedContentIsArray) {
      this[contentTypeId].data = [];
    }
    content.forEach(contentItem => {
      const fields = contentItem.fields;
      // If the cached content is an array then push the content, otherwise assign it directly
      if (cachedContentIsArray || contentTypeId === 'landingPage' || contentTypeId === 'landingPageComponent') {
        this[contentTypeId].data.push(fields);
      } else {
        this[contentTypeId].language = selectedLanguageCode;
        Object.assign(this[contentTypeId].data, fields);
      }
    });
    // If there is a transform data variable then alert the behaviour subscribers to do their
    // respective translations
    this.emitTransformDataBehavior(contentTypeId);
  }

  /** Function to emit the transform data behaviour if necessary **/
  emitTransformDataBehavior(contentTypeId) {
    if (this[contentTypeId].transformData) {
      this[contentTypeId].transformData.next(true);
    }
  }

  /** Function to fetch all the relevant content in the app **/
  fetchAll() {
    const keys = Object.keys(this);
    keys.forEach(contentType => {
      // Make sure it is not one of the injected services
      if (contentType !== 'dataService' && contentType !== 'contentful') {
        this.fetchContent(contentType);
      }
    });
  }
}
